import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha,
} from "@mui/material";
import {
  EventAvailable as LeaveIcon,
  Add as AddIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

const StyledCard = styled(Card)(({ theme }) => ({
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: "blur(10px)",
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[10],
}));

const LeaveRequestTab: React.FC = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [leaveType, setLeaveType] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");

  const leaveBalance = {
    vacation: { available: 15, used: 5, total: 20 },
    sick: { available: 8, used: 2, total: 10 },
    personal: { available: 3, used: 0, total: 3 },
  };

  const leaveRequests = [
    {
      id: 1,
      type: "Vacation",
      startDate: "2024-12-20",
      endDate: "2024-12-31",
      days: 8,
      status: "Approved",
      approver: "John Manager",
    },
    {
      id: 2,
      type: "Sick Leave",
      startDate: "2024-11-15",
      endDate: "2024-11-16",
      days: 2,
      status: "Approved",
      approver: "John Manager",
    },
    {
      id: 3,
      type: "Personal",
      startDate: "2024-10-10",
      endDate: "2024-10-10",
      days: 1,
      status: "Pending",
      approver: "John Manager",
    },
  ];

  const leaveTypes = ["Vacation", "Sick Leave", "Personal", "Maternity/Paternity", "Emergency"];

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved":
        return "success";
      case "pending":
        return "warning";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  const handleSubmitRequest = () => {
    console.log("Submitting leave request:", { leaveType, startDate, endDate, reason });
    setOpenDialog(false);
    setLeaveType("");
    setStartDate("");
    setEndDate("");
    setReason("");
  };

  return (
    <Grid container spacing={3}>
      {/* Leave Balance Cards */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
          <CalendarIcon color="primary" sx={{ mr: 1 }} />
          Leave Balance
        </Typography>
      </Grid>

      <Grid item xs={12} sm={4}>
        <StyledCard>
          <CardContent sx={{ textAlign: "center" }}>
            <Typography variant="h4" color="primary" gutterBottom>
              {leaveBalance.vacation.available}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Vacation Days Available
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {leaveBalance.vacation.used} used / {leaveBalance.vacation.total} total
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={4}>
        <StyledCard>
          <CardContent sx={{ textAlign: "center" }}>
            <Typography variant="h4" color="info.main" gutterBottom>
              {leaveBalance.sick.available}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Sick Days Available
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {leaveBalance.sick.used} used / {leaveBalance.sick.total} total
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={4}>
        <StyledCard>
          <CardContent sx={{ textAlign: "center" }}>
            <Typography variant="h4" color="success.main" gutterBottom>
              {leaveBalance.personal.available}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Personal Days Available
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {leaveBalance.personal.used} used / {leaveBalance.personal.total} total
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      {/* Leave Requests */}
      <Grid item xs={12}>
        <StyledCard>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
                <LeaveIcon color="primary" sx={{ mr: 1 }} />
                Leave Requests
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenDialog(true)}
              >
                New Request
              </Button>
            </Box>
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Start Date</strong></TableCell>
                    <TableCell><strong>End Date</strong></TableCell>
                    <TableCell><strong>Days</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Approver</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leaveRequests.map((request) => (
                    <TableRow
                      key={request.id}
                      hover
                      sx={{ "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.05) } }}
                    >
                      <TableCell>{request.type}</TableCell>
                      <TableCell>{request.startDate}</TableCell>
                      <TableCell>{request.endDate}</TableCell>
                      <TableCell>{request.days}</TableCell>
                      <TableCell>
                        <Chip
                          label={request.status}
                          color={getStatusColor(request.status) as any}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{request.approver}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </StyledCard>
      </Grid>

      {/* New Leave Request Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Leave</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Leave Type"
                value={leaveType}
                onChange={(e) => setLeaveType(e.target.value)}
              >
                {leaveTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason (Optional)"
                multiline
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmitRequest} variant="contained">
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default LeaveRequestTab;